import { useEffect, useRef, type ReactNode } from 'react'

// Existing project dialogs are page-specific and have no reusable focus management.
export function M2cConfirmation({ label, onClose, children }: { label: string; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)
  const close = useRef(onClose)
  close.current = onClose
  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const dialog = ref.current!
    const focusable = () => Array.from(dialog.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]'))
    const safeFocus = () => dialog.querySelector<HTMLElement>('[data-m2c-cancel]:not(:disabled)') ?? dialog
    safeFocus().focus()
    function keydown(event: KeyboardEvent) {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close.current(); return }
      if (event.key !== 'Tab') return
      const items = focusable()
      const first = items[0], last = items[items.length - 1]
      if (!first || !last) { event.preventDefault(); dialog.focus(); return }
      if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) { event.preventDefault(); last.focus() }
      else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) { event.preventDefault(); first.focus() }
    }
    function keepFocus(event: FocusEvent) {
      if (!dialog.contains(event.target as Node)) safeFocus().focus()
    }
    document.addEventListener('keydown', keydown, true)
    document.addEventListener('focusin', keepFocus)
    return () => {
      document.removeEventListener('keydown', keydown, true)
      document.removeEventListener('focusin', keepFocus)
      previous?.focus()
      // A successful write disables its trigger; preserve a useful keyboard
      // position instead of dropping focus onto the document body.
      const region = previous?.closest<HTMLElement>('[role="region"]')
      if (previous?.isConnected && document.activeElement !== previous && region) {
        region.tabIndex = -1
        region.focus()
      }
    }
  }, [])
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--background)] p-4">
    <div ref={ref} role="alertdialog" aria-modal="true" aria-label={label} tabIndex={-1}
      className="w-full max-w-lg space-y-3 rounded-lg border border-[var(--border)] bg-[var(--card)] p-4 focus-visible:ring-2 focus-visible:ring-[var(--ring)]">
      {children}
    </div>
  </div>
}
